import React, { useState, useLayoutEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import PageTitle from '@/components/common/PageTitle';
import HookFormControls, { HookFormControlsProps } from '@/components/common/HookFormControls';
import { FormProvider, useForm } from 'react-hook-form';
import LoadingButton from '@mui/lab/LoadingButton';
import { useSnackbar } from 'notistack';
import { Messages } from '@/common/constants';
import Loading from '@/components/common/Loading';
import { Department, ParentDepartment } from '@/types/Department.type';
import DepartmentService from '@/services/Department.service';
import URL from '@/common/routes/Url';
import useSidebar from '@/common/hooks/useSidebar';

const DepartmentDetail: React.FC = () => {
  const pathParams = useParams<{ id: string }>();
  const useFormMethods = useForm<Department & ParentDepartment>({ shouldUnregister: false });
  const {
    handleSubmit,
    formState: { isSubmitting },
    reset,
  } = useFormMethods;
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [department, setDepartment] = useState<Department[] | []>([]);
  const [loading, setLoading] = useState(true);

  const inputs: Array<HookFormControlsProps> = [
    {
      name: 'name',
      type: 'text',
      label: '部署名*',
      rule: {
        required: '部署名は必須項目です。',
        maxLength: {
          value: 40,
          message: '部署名は40文字以内で入力してください。',
        },
      },
    },
    {
      name: 'shortName',
      type: 'text',
      label: '略名*',
      rule: {
        required: '略名は必須項目です。',
        maxLength: {
          value: 20,
          message: '略名は20文字以内で入力してください。',
        },
      },
    },
    {
      name: 'sortNo',
      type: 'number',
      label: '表示順',
      inputProps: { min: 1 },
      rule: {
        max: { value: 9999, message: '表示順は9,999字以内で入力してください。' },
        min: { value: 1, message: '表示順は1以上で入力してください。' },
      },
    },
    {
      name: 'parentDepartment',
      type: 'autocomplete',
      label: '親部署',
      options: department,
      getOptionLabel: 'name',
    },
  ];

  useLayoutEffect(() => {
    (async () => {
      try {
        if (pathParams.id) {
          const department = await DepartmentService.get(pathParams.id);
          department.data.sortNo = department.data.sortNo ?? '';
          reset(department.data);
        }
        const parentDepartments = await DepartmentService.getAll({ params: { parent: 1, excludesId: pathParams.id } });
        setDepartment(parentDepartments.data);
        setLoading(false);
      } catch (error) {
        setLoading(false);
        enqueueSnackbar(Messages.select.failure);
      }
    })();
  }, []);

  const { reload } = useSidebar();
  const onSubmit = async (values: Department & ParentDepartment) => {
    try {
      const requestValues = { ...values, ...{ parentId: values.parentDepartment ? values.parentDepartment.id : null } };
      if (pathParams.id) {
        await DepartmentService.update(requestValues, pathParams.id);
        enqueueSnackbar(Messages.update.success, { variant: 'info' });
      } else {
        await DepartmentService.create(requestValues);
        enqueueSnackbar(Messages.create.success, { variant: 'info' });
      }
      reload();
      navigate(URL.departments);
    } catch (error: any) {
      enqueueSnackbar(error.response.data.message);
      return;
    }
  };

  if (loading) {
    return <Loading />;
  }
  return (
    <Grid container justifyContent="center">
      <Grid item xs={12} lg={4}>
        <Box sx= {{ display: { xs: 'block', sm: 'none' } }}>
          <PageTitle>部署マスタ ー 編集</PageTitle>
        </Box>
        <Paper variant="outlined" sx={{ my: { xs: 0, md: 0 }, p: { xs: 2, md: 3 } }}>
          <FormProvider {...useFormMethods}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <Grid container spacing={3}>
                {inputs.map((input, index) => (
                  <Grid item key={index} xs={12}>
                    <HookFormControls key={index} {...input} />
                  </Grid>
                ))}
                <Grid item xs={12}>
                  <Grid container justifyContent="flex-end">
                    <Stack direction="row" spacing={2}>
                      <Button
                        variant="outlined"
                        onClick={() => {
                          navigate(-1);
                        }}
                      >
                        閉じる
                      </Button>
                      <LoadingButton variant="contained" type="submit" color="primary" loading={isSubmitting}>
                        登録
                      </LoadingButton>
                    </Stack>
                  </Grid>
                </Grid>
              </Grid>
            </form>
          </FormProvider>
        </Paper>
      </Grid>
    </Grid>
  );
};
export default DepartmentDetail;
